import { BaseService } from "./BaseService";
import type {
  AnalyticsOverviewRangeQuery,
  OfferConversionResponse,
  OfferConversionSide,
  OrdersByDayResponse,
  PreCheckInByDayResponse,
  PreCheckOutByDayResponse,
  RatingsByStayResponse,
} from "@/types/analytics-overview";

type AnalyticsOverviewRangeExportQuery = AnalyticsOverviewRangeQuery & {
  format: "csv" | "xlsx";
};

type OfferConversionExportQuery = AnalyticsOverviewRangeExportQuery & {
  side?: OfferConversionSide;
};

type OrdersByDayExportQuery = AnalyticsOverviewRangeExportQuery & {
  breakdownByType?: boolean;
};

class AnalyticsOverviewService extends BaseService {
  private readonly basePath = "/analytics/overview";

  getOfferConversion(
    query: AnalyticsOverviewRangeQuery & { side?: OfferConversionSide },
  ): Promise<OfferConversionResponse> {
    return this.get<OfferConversionResponse>(
      `${this.basePath}/offers/conversion`,
      query,
    );
  }

  getRatingsByStay(
    query: AnalyticsOverviewRangeQuery,
  ): Promise<RatingsByStayResponse> {
    return this.get<RatingsByStayResponse>(
      `${this.basePath}/ratings/by-stay`,
      query,
    );
  }

  getOrdersByDay(
    query: AnalyticsOverviewRangeQuery & { breakdownByType?: boolean },
  ): Promise<OrdersByDayResponse> {
    return this.get<OrdersByDayResponse>(
      `${this.basePath}/orders/by-day`,
      query,
    );
  }

  getPreCheckInByDay(
    query: AnalyticsOverviewRangeQuery,
  ): Promise<PreCheckInByDayResponse> {
    return this.get<PreCheckInByDayResponse>(
      `${this.basePath}/pre-check-in/by-day`,
      query,
    );
  }

  getPreCheckOutByDay(
    query: AnalyticsOverviewRangeQuery,
  ): Promise<PreCheckOutByDayResponse> {
    return this.get<PreCheckOutByDayResponse>(
      `${this.basePath}/pre-check-out/by-day`,
      query,
    );
  }

  exportOfferConversion(query: OfferConversionExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/offers/conversion/export`, query);
  }

  exportRatingsByStay(query: AnalyticsOverviewRangeExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/ratings/by-stay/export`, query);
  }

  exportOrdersByDay(query: OrdersByDayExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/orders/by-day/export`, query);
  }
}

export const analyticsOverviewService = new AnalyticsOverviewService();
